import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { ClockIcon, FileDownIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";


import type {
  OperationsParams,
  OrderThroughputParams,
} from "../api/client";
import { reportsApi } from "../api/client";
import type { AvgPrepTimeResult } from "../../domain/repo/get-avg-prep-time";
import type { OrderThroughputItem } from "../../domain/repo/get-order-throughput";
import { OperationsReportsContent } from "../ui/operations/OperationsReportsContent";
import { OperationsReportsFilter } from "../ui/operations/OperationsReportsFilter";

function exportOperationsToCsv(
  avgPrepTime: AvgPrepTimeResult[],
  throughput: OrderThroughputItem[],
  groupBy: "hour" | "day",
) {
  // Average Prep Time section
  const prepTimeHeaders = [
    "ສາຂາ",
    "ຊ່ອງທາງ",
    "ເວລາເຕີມສະເລ່ຍ (ນາທີ)",
    "ຈຳນວນອໍເດີ",
  ];

  const prepTimeRows = avgPrepTime.map((item) => [
    item.branchName ?? "ທັງໝົດ",
    item.channel === "APP" ? "ແອັບ" : item.channel === "WALKIN" ? "ມາຮ້ານ" : "ທັງໝົດ",
    String(item.avgPrepTimeMinutes),
    String(item.totalOrders),
  ]);

  // Throughput section
  const throughputHeaders = [
    groupBy === "hour" ? "ຊ່ວງເວລາ" : "ວັນທີ",
    "ຈຳນວນອໍເດີ",
    "ສາຂາ",
  ];

  const throughputRows = throughput.map((item) => [
    item.period,
    String(item.orderCount),
    item.branchName ?? "ທັງໝົດ",
  ]);

  // Combine sections with empty line separator
  const csvContent = [
    "=== ເວລາເຕີມສະເລ່ຍ ===",
    prepTimeHeaders.join(","),
    ...prepTimeRows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    ),
    "",
    "=== ຈຳນວນອໍເດີຕໍ່" + (groupBy === "hour" ? "ຊົ່ວໂມງ" : "ວັນ") + " ===",
    throughputHeaders.join(","),
    ...throughputRows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    ),
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const dateStr = new Date().toISOString().slice(0, 10);
  a.download = `operations-report-${dateStr}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function OperationsReportsPage() {
  // Default to last 30 days - use useMemo to prevent infinite loops
  const defaultFilters = useMemo(() => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 30);
    
    return {
      fromDate,
      toDate,
    };
  }, []);

  const [filters, setFilters] = useState<OperationsParams>(defaultFilters);
  const [groupBy, setGroupBy] = useState<"hour" | "day">("day");
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const exportParams: OrderThroughputParams = {
        ...filters,
        groupBy,
      };
      const result = await reportsApi.getOperationsExport(exportParams);
      
      if (
        result.avgPrepTime.length === 0 &&
        result.throughput.length === 0
      ) {
        toast.info("ບໍ່ມີຂໍ້ມູນສົ່ງອອກ");
        return;
      }

      exportOperationsToCsv(result.avgPrepTime, result.throughput, groupBy);
      const totalItems =
        result.avgPrepTime.length + result.throughput.length;
      toast.success(
        `ສົ່ງອອກ ${totalItems.toLocaleString("lo-LA")} ລາຍການແລ້ວ`,
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error("ສົ່ງອອກລົ້ມເຫຼວ");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ລາຍງານການດຳເນີນງານ",
              to: "/app/reports/operations",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <ClockIcon className="size-5" />
              ລາຍງານການດຳເນີນງານ
            </h2>
            <p className="text-muted-foreground">
              ວິເຄາະປະສິດທິພາບການດຳເນີນງານ ເວລາເຕີມສະເລ່ຍ ແລະ ຈຳນວນອໍເດີຕໍ່ຊົ່ວໂມງ/ວັນ
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting}
            className="flex items-center gap-2"
          >
            <FileDownIcon className="size-4" />
            {isExporting ? "ກຳລັງສົ່ງອອກ…" : "ສົ່ງອອກ (CSV)"}
          </Button>
        </div>

        <OperationsReportsFilter
          filters={filters}
          onFiltersChange={setFilters}
        />

        <OperationsReportsContent
          filters={filters}
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
        />
      </Main>
    </>
  );
}
